import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, TextField, Button,
  Paper, Chip, Stack, Alert, CircularProgress,
  IconButton, Tooltip, Fade,
} from "@mui/material";
import {
  ArrowBack, ArrowForward, CheckCircle, ContentCopy,
  School, Send as SendIcon, FavoriteRounded, OndemandVideo,
} from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { apiClient, authService } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { trackSignupComplete } from "../analytics";
import { clearLeafletCart } from "../utils/leafletCart";
import CheckoutAccountForm, {
  EMPTY_CHECKOUT_ACCOUNT, validateCheckoutAccount,
  type CheckoutAccountErrors, type CheckoutAccountMode, type CheckoutAccountValues,
} from "../components/checkout/CheckoutAccountForm";
import { LbpSettings, DEFAULT_LBP_SETTINGS, getLbpSettings, formatLbpOnly } from "../utils/currency";
import { VIDEO_ADDON_PLAN_ID } from "../utils/planPricing";

// Map frontend plan IDs to backend plan values
const PLAN_TO_BACKEND: Record<string, string> = {
  starter: "basic",
  pro: "premium",
  elite: "pro",
  // The video-only add-on uses the same id on both sides.
  [VIDEO_ADDON_PLAN_ID]: VIDEO_ADDON_PLAN_ID,
};

export interface PaymentConfig {
  wish_money: { number: string; active: boolean };
  omt: { number: string; active: boolean };
  bank: { iban: string; bankName: string; accountName: string; active: boolean };
  whatsapp: { number: string; active: boolean };
}

export const DEFAULT_PAYMENT_CONFIG: PaymentConfig = {
  wish_money: { number: "+961 XX XXX XXX", active: true },
  omt: { number: "+961 XX XXX XXX", active: true },
  bank: { iban: "LB00 0000 0000 0000", bankName: "Bank Name", accountName: "Account Holder", active: false },
  whatsapp: { number: "+961 XX XXX XXX", active: false },
};

export interface PaymentState {
  planId: string;
  planName: string;
  /**
   * The figure the buyer was shown, video add-ons included. Checkout sends it
   * back and the server rejects the order if it disagrees with the catalog, so
   * nobody is ever charged something other than what was on screen.
   */
  price: number;
  billing: string;
  selectedCourseIds?: string[];
  selectedCourseTitles?: string[];
  /**
   * Leaflets bought with the companion video. A subset of `selectedCourseIds`
   * on a plan; on the video-only add-on it is the whole order and
   * `selectedCourseIds` stays empty, since nothing new is being unlocked.
   */
  videoCourseIds?: string[];
  /** Display titles for `videoCourseIds`, in the same order. */
  videoCourseTitles?: string[];
  /** Unlimited plan only — every companion video included. */
  allVideosIncluded?: boolean;
}

const NAVY = "#0A1628";
const BLUE = "#1565C0";
const MUTED = "#6B7280";

/**
 * Whish Money's brand mark, served from `public/`. Drop the official artwork in
 * as `public/whish-logo.png` (square, ideally 128px+) and it appears here; until
 * then the tile falls back to the heart on Whish red, so a missing file never
 * renders as a broken image.
 */
const WHISH_LOGO_SRC = "/whish-logo.png";

const WhishBadge: React.FC = () => {
  const [logoOk, setLogoOk] = useState(true);
  return (
    <Box sx={{
      width: 46, height: 46, borderRadius: "13px", flexShrink: 0, overflow: "hidden",
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #F87171 0%, #DC2626 100%)",
      boxShadow: "0 6px 16px rgba(220,38,38,0.4)",
    }}>
      {logoOk ? (
        <Box
          component="img"
          src={WHISH_LOGO_SRC}
          alt=""
          onError={() => setLogoOk(false)}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <FavoriteRounded sx={{ color: "#fff", fontSize: 26 }} />
      )}
    </Box>
  );
};

const METHOD_META: Record<string, { emoji: string; color: string }> = {
  wish_money: { emoji: "❤️", color: "#DC2626" },
  omt:        { emoji: "🟠", color: "#EA580C" },
  bank:       { emoji: "🏦", color: "#1565C0" },
  whatsapp:   { emoji: "💬", color: "#16A34A" },
};

const PaymentRequest: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login } = useAuth();

  // Restore state from sessionStorage when returning from auth redirect
  const locationState = location.state as PaymentState | undefined;
  const sessionState: PaymentState | null = (() => {
    try { return JSON.parse(sessionStorage.getItem("pending_payment_state") || "null"); }
    catch { return null; }
  })();
  const state: PaymentState | null = locationState ?? sessionState;
  // A video-only order is not a plan — it adds nothing but the video, so the
  // "Plan" wording and the leaflet summary don't apply.
  const isVideoAddon = state?.planId === VIDEO_ADDON_PLAN_ID;

  const [step, setStep]               = useState<1 | 2 | 3>(1);
  const [config, setConfig]           = useState<PaymentConfig>(DEFAULT_PAYMENT_CONFIG);
  const [copied, setCopied]           = useState("");
  const [selectedMethod, setSelectedMethod] = useState("wish_money");
  const [payerName, setPayerName]     = useState((user as any)?.name || "");
  const [payerPhone, setPayerPhone]   = useState((user as any)?.phone || "");
  const [reference, setReference]     = useState("");
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [lbpSettings, setLbpSettings] = useState<LbpSettings>(DEFAULT_LBP_SETTINGS);
  const [couponCode, setCouponCode]       = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountedPrice: number } | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError]     = useState("");
  const [whishLoading, setWhishLoading]   = useState(false);
  // Visitors create their account (or log in) right here instead of on a
  // separate sign-up page — see ensureAccount().
  const [accountMode, setAccountMode]     = useState<CheckoutAccountMode>("register");
  const [account, setAccount]             = useState<CheckoutAccountValues>(EMPTY_CHECKOUT_ACCOUNT);
  const [accountErrors, setAccountErrors] = useState<CheckoutAccountErrors>({});
  const [accountNotice, setAccountNotice] = useState("");
  const [accountBusy, setAccountBusy]     = useState(false);

  useEffect(() => {
    // Try API first, then fall back to localStorage
    apiClient.get<{ value: PaymentConfig }>("/settings/payment-methods")
      .then(res => { if (res.data?.value) setConfig(res.data.value); })
      .catch(() => {
        const local = localStorage.getItem("payment_methods_config");
        if (local) { try { setConfig(JSON.parse(local)); } catch {} }
      });
    getLbpSettings().then(setLbpSettings);
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(""), 2000);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !state) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const code = couponCode.trim().toUpperCase();
      const res = await apiClient.post<{ valid: boolean; discountedPrice: number; message?: string }>(
        "/coupons/validate",
        { code, planId: PLAN_TO_BACKEND[state.planId] ?? state.planId, price: state.price }
      );
      if (res.data.valid) {
        setAppliedCoupon({ code, discountedPrice: res.data.discountedPrice });
      } else {
        setCouponError(res.data.message || "This coupon code isn't valid.");
      }
    } catch (err: any) {
      setCouponError(err?.response?.data?.message || "Couldn't validate this coupon. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  const changeAccountMode = (mode: CheckoutAccountMode) => {
    setAccountMode(mode);
    setAccountErrors({});
    setAccountNotice("");
  };

  /**
   * Makes sure there is a signed-in account to attach the order to. Signed-in
   * buyers pass straight through; visitors are registered (or logged in) with
   * the details from the form. Returns false, with the problem shown on the
   * form, when that can't happen — the order is never sent without an owner.
   */
  const ensureAccount = async (): Promise<boolean> => {
    if (user) return true;
    const errors = validateCheckoutAccount(account, accountMode);
    setAccountErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError("Please complete your details above.");
      return false;
    }

    setAccountBusy(true);
    try {
      const email = account.email.trim().toLowerCase();
      const signedIn = accountMode === "login"
        ? await authService.login({ email, password: account.password })
        : await authService.register({
            name: account.name.trim(), email, password: account.password,
            phone: account.phone.trim(), type: "user",
          });
      login(signedIn);
      if (accountMode === "register") trackSignupComplete("checkout");
      // The manual-transfer step asks for these again; don't make them retype.
      setPayerName((prev: string) => prev || signedIn.name || account.name.trim());
      setPayerPhone((prev: string) => prev || signedIn.phone || account.phone.trim());
      setAccount(EMPTY_CHECKOUT_ACCOUNT);
      return true;
    } catch (err: any) {
      const message: string = err?.message || "";
      if (accountMode === "register" && /already registered/i.test(message)) {
        // Keep the email, drop the password they typed for the new account.
        setAccountMode("login");
        setAccount(prev => ({ ...prev, password: "" }));
        setAccountNotice("This email already has an account. Enter its password to log in and finish your order.");
        setAccountErrors({});
        setError("");
      } else if (accountMode === "login") {
        setAccountErrors({ password: "The email or password is incorrect." });
        setError("");
      } else {
        setError(message || "We couldn't create your account. Please try again.");
      }
      return false;
    } finally {
      setAccountBusy(false);
    }
  };

  // Real-time Whish Pay checkout — redirects to Whish's hosted payment page,
  // which handles phone/OTP collection itself, then calls back to activate.
  const handleWhishPay = async () => {
    if (!state) return;
    setError("");
    setWhishLoading(true);
    if (!(await ensureAccount())) { setWhishLoading(false); return; }
    try {
      const res = await apiClient.post<{ data: { subscriptionId: string; collectUrl: string } }>(
        "/payments/whish/initiate",
        {
          plan: PLAN_TO_BACKEND[state.planId] ?? "basic",
          billingCycle: "lifetime",
          price: state.price,
          currency: "USD",
          couponCode: appliedCoupon?.code,
          selectedCourseIds: state.selectedCourseIds ?? [],
          videoCourseIds: state.videoCourseIds ?? [],
          allVideosIncluded: !!state.allVideosIncluded,
          features: [],
          metadata: {
            isPreRecorded: true,
            planLabel: state.planName,
            selectedCourseTitles: state.selectedCourseTitles ?? [],
            videoCourseTitles: state.videoCourseTitles ?? [],
          },
        }
      );
      sessionStorage.removeItem("pending_payment_state");
      window.location.href = res.data.data.collectUrl;
    } catch (err: any) {
      setError(err?.response?.data?.message || "Couldn't start the Whish payment. Please try again.");
      setWhishLoading(false);
    }
  };

  if (!state) {
    return (
      <Container maxWidth="sm" sx={{ mt: 12, textAlign: "center" }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          No plan selected. Please go back and choose a plan.
        </Typography>
        <Button variant="contained" onClick={() => navigate("/")}
          sx={{ borderRadius: "50px", px: 4, fontWeight: 700, bgcolor: BLUE }}>
          View Plans
        </Button>
      </Container>
    );
  }

  const handleSubmit = async () => {
    setError("");
    if (!payerName.trim())  { setError("Please enter your full name."); return; }
    if (!payerPhone.trim()) { setError("Please enter your phone number."); return; }
    if (!reference.trim())  { setError("Please enter your transaction reference number."); return; }

    setLoading(true);
    try {
      // Create the subscription. The backend auto-activates it, but we set
      // pendingVerification:true in metadata so Classes.tsx blocks access until
      // the admin explicitly activates (which clears the flag).
      await apiClient.post("/subscriptions", {
        plan: PLAN_TO_BACKEND[state.planId] ?? "basic",
        billingCycle: "lifetime",
        price: state.price,
        couponCode: appliedCoupon?.code,
        selectedCourseIds: state.selectedCourseIds ?? [],
        videoCourseIds: state.videoCourseIds ?? [],
        allVideosIncluded: !!state.allVideosIncluded,
        features: [],
        paymentMethod: selectedMethod,
        metadata: {
          isPreRecorded: true,
          pendingVerification: true,
          payerName,
          payerPhone,
          reference,
          planLabel: state.planName,
          submittedAt: new Date().toISOString(),
          selectedCourseTitles: state.selectedCourseTitles ?? [],
          videoCourseTitles: state.videoCourseTitles ?? [],
          couponCode: appliedCoupon?.code,
        },
      });

      sessionStorage.removeItem("pending_payment_state");
      clearLeafletCart();
      setStep(3);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Build the active methods list from config
  const activeMethods = [
    config.wish_money?.active && {
      key: "wish_money",
      label: "Whish Money",
      value: "Instant, secure checkout with your Whish wallet",
      ...METHOD_META.wish_money,
    },
    config.omt?.active && {
      key: "omt",
      label: "OMT",
      value: config.omt.number,
      ...METHOD_META.omt,
    },
    config.bank?.active && {
      key: "bank",
      label: `${config.bank.bankName} — ${config.bank.accountName}`,
      value: config.bank.iban,
      ...METHOD_META.bank,
    },
    config.whatsapp?.active && {
      key: "whatsapp",
      label: "WhatsApp",
      value: config.whatsapp.number,
      ...METHOD_META.whatsapp,
    },
  ].filter(Boolean) as { key: string; label: string; value: string; emoji: string; color: string }[];

  // ── Step 3: Success ──────────────────────────────────────────
  if (step === 3) {
    return (
      <Container maxWidth="sm" sx={{ pt: 12, pb: 6, textAlign: "center" }}>
        <Fade in>
          <Box>
            <Box sx={{
              width: 88, height: 88, borderRadius: "50%",
              bgcolor: "#ECFDF5", mx: "auto", mb: 3,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CheckCircle sx={{ fontSize: 52, color: "#10B981" }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, mb: 1 }}>
              Request Submitted!
            </Typography>
            <Typography sx={{ color: MUTED, mb: 1 }}>
              Your <strong>{state.planName}</strong> {isVideoAddon ? "add-on" : "subscription"} request has been received.
            </Typography>
            <Typography sx={{ color: MUTED, mb: 4 }}>
              Our team will verify your payment and activate your account within 24 hours.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="outlined" onClick={() => navigate("/")}
                sx={{ borderRadius: "50px", px: 3, fontWeight: 600, borderColor: BLUE, color: BLUE }}>
                Back to Home
              </Button>
              <Button variant="contained" onClick={() => navigate("/membership/classes")}
                sx={{ borderRadius: "50px", px: 3, fontWeight: 700, bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" } }}>
                Go to Classes
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Helmet>
        <title>Complete Your Payment — Dr.Paw</title>
        <meta name="description" content="Complete your Dr.Paw subscription payment via Wish Money, OMT, bank transfer, or WhatsApp." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <HreflangTags />
      {/* Back */}
      <Button startIcon={<ArrowBack />}
        onClick={() => step === 2 ? setStep(1) : navigate(-1)}
        sx={{ mb: 3, textTransform: "none", color: MUTED, fontWeight: 600 }}>
        Back
      </Button>

      {/* Progress bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {[1, 2].map(s => (
          <Box key={s} sx={{
            height: 4, borderRadius: "2px", flex: 1,
            bgcolor: step >= s ? BLUE : "#E5E7EB",
            transition: "background-color 0.3s ease",
          }} />
        ))}
      </Stack>

      {/* Plan chip */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap">
        {appliedCoupon ? (
          <>
            <Typography sx={{ color: MUTED, textDecoration: "line-through", fontSize: "0.85rem" }}>
              ${state.price}
            </Typography>
            <Chip
              label={isVideoAddon
                ? `${state.planName} — $${appliedCoupon.discountedPrice}`
                : `${state.planName} Plan — $${appliedCoupon.discountedPrice}/mo`}
              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700, border: "1px solid #A5D6A7" }}
            />
          </>
        ) : (
          <Chip
            label={isVideoAddon
              ? `${state.planName} — $${state.price}`
              : `${state.planName} Plan — $${state.price}/mo`}
            sx={{ bgcolor: "#EEF4FF", color: BLUE, fontWeight: 700, border: "1px solid #BBDEFB" }}
          />
        )}
        {lbpSettings.showLbp && (
          <Typography variant="caption" sx={{ color: MUTED }}>
            {formatLbpOnly(appliedCoupon?.discountedPrice ?? state.price, lbpSettings)}
          </Typography>
        )}
      </Stack>

      {/* Coupon field */}
      <Box sx={{ mb: 2.5 }}>
        {appliedCoupon ? (
          <Chip
            label={`✓ Coupon ${appliedCoupon.code} applied`}
            onDelete={handleRemoveCoupon}
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ) : (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Have a coupon?"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <Button
              variant="outlined"
              onClick={handleApplyCoupon}
              disabled={couponLoading || !couponCode.trim()}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: BLUE, color: BLUE, whiteSpace: "nowrap" }}
            >
              {couponLoading ? <CircularProgress size={16} /> : "Apply"}
            </Button>
          </Stack>
        )}
        {couponError && (
          <Typography variant="caption" sx={{ color: "#EF4444", display: "block", mt: 0.5 }}>
            {couponError}
          </Typography>
        )}
      </Box>

      {/* ── Step 1: Payment Instructions ─────────────────────── */}
      {step === 1 && (
        <Fade in key="step1">
          <Box>
            {user ? (
              <Typography sx={{ color: MUTED, fontSize: "0.85rem", mb: 2 }}>
                Signed in as <strong>{user.email}</strong>
              </Typography>
            ) : (
              <CheckoutAccountForm
                mode={accountMode}
                onModeChange={changeAccountMode}
                values={account}
                onChange={v => { setAccount(v); setError(""); }}
                errors={accountErrors}
                notice={accountNotice}
                disabled={accountBusy || whishLoading}
              />
            )}

            <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, mb: 0.5 }}>
              Choose How to Pay
            </Typography>
            <Typography sx={{ color: MUTED, mb: 3 }}>
              You'll pay <strong>${appliedCoupon?.discountedPrice ?? state.price}</strong>{lbpSettings.showLbp ? ` ${formatLbpOnly(appliedCoupon?.discountedPrice ?? state.price, lbpSettings)}` : ""}. Pick a method below.
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {activeMethods.map(m => (
                <Paper key={m.key} elevation={0} onClick={() => setSelectedMethod(m.key)} sx={{
                  p: 2, borderRadius: "16px", cursor: "pointer",
                  border: "1.5px solid",
                  borderColor: selectedMethod === m.key ? m.color : "#E5E7EB",
                  bgcolor: selectedMethod === m.key ? `${m.color}0D` : "#FAFAFA",
                  display: "flex", alignItems: "center", gap: 2,
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: m.color, bgcolor: `${m.color}08` },
                }}>
                  {m.key === "wish_money" ? (
                    <WhishBadge />
                  ) : (
                    <Box sx={{
                      width: 46, height: 46, borderRadius: "13px", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: `${m.color}14`, border: `1px solid ${m.color}22`,
                    }}>
                      <Typography sx={{ fontSize: "1.5rem", lineHeight: 1, userSelect: "none" }}>
                        {m.emoji}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 700, fontSize: "0.75rem", color: MUTED,
                      textTransform: "uppercase", letterSpacing: "0.06em",
                    }}>
                      {m.label}
                    </Typography>
                    <Typography sx={{
                      fontWeight: m.key === "wish_money" ? 600 : 800,
                      fontSize: m.key === "wish_money" ? "0.9rem" : "1.05rem",
                      color: m.key === "wish_money" ? MUTED : NAVY,
                      letterSpacing: "0.03em",
                      wordBreak: m.key === "wish_money" ? "normal" : "break-all",
                      lineHeight: 1.4,
                    }}>
                      {m.value}
                    </Typography>
                  </Box>
                  {m.key !== "wish_money" && (
                    <Tooltip title={copied === m.value ? "Copied!" : "Copy number"}>
                      <IconButton size="small"
                        onClick={e => { e.stopPropagation(); handleCopy(m.value); }}
                        sx={{ color: m.color, flexShrink: 0, "&:hover": { bgcolor: `${m.color}18` } }}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Paper>
              ))}

              {activeMethods.length === 0 && (
                <Alert severity="info" sx={{ borderRadius: "12px" }}>
                  Payment method details are being configured. Please contact support.
                </Alert>
              )}
            </Stack>

            {copied && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: "12px" }}>
                Copied to clipboard!
              </Alert>
            )}

            {/* Selected courses summary */}
            {(state.selectedCourseTitles ?? []).length > 0 && (
              <Paper elevation={0} sx={{
                p: 2, mb: 3, borderRadius: "14px",
                bgcolor: "#F0F7FF", border: "1px solid #BBDEFB",
              }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <School sx={{ color: BLUE, fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, color: BLUE, fontSize: "0.85rem" }}>
                    Selected Courses
                  </Typography>
                </Stack>
                <Stack spacing={0.4}>
                  {state.selectedCourseTitles!.map((title, i) => {
                    const withVideo = !!state.videoCourseIds?.includes(state.selectedCourseIds?.[i] ?? "");
                    return (
                      <Typography key={i} sx={{ color: NAVY, fontSize: "0.88rem" }}>
                        • {title}
                        {withVideo && (
                          <Box component="span" sx={{ color: BLUE, fontWeight: 700, ml: 0.75, fontSize: "0.8rem" }}>
                            + companion video
                          </Box>
                        )}
                      </Typography>
                    );
                  })}
                </Stack>
              </Paper>
            )}

            {/* Video-only add-on summary — the leaflet is already owned */}
            {isVideoAddon && (state.videoCourseTitles ?? []).length > 0 && (
              <Paper elevation={0} sx={{
                p: 2, mb: 3, borderRadius: "14px",
                bgcolor: "#F0F7FF", border: "1px solid #BBDEFB",
              }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <OndemandVideo sx={{ color: BLUE, fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, color: BLUE, fontSize: "0.85rem" }}>
                    Companion Video For
                  </Typography>
                </Stack>
                <Stack spacing={0.4}>
                  {state.videoCourseTitles!.map((title, i) => (
                    <Typography key={i} sx={{ color: NAVY, fontSize: "0.88rem" }}>
                      • {title}
                    </Typography>
                  ))}
                </Stack>
                <Typography variant="caption" sx={{ color: MUTED, display: "block", mt: 1 }}>
                  You already own this leaflet — this only adds its video.
                </Typography>
              </Paper>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>
            )}

            {selectedMethod === "wish_money" ? (
              <>
                <Button fullWidth variant="contained" size="large"
                  endIcon={whishLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                  onClick={handleWhishPay}
                  disabled={whishLoading || activeMethods.length === 0}
                  sx={{
                    py: 1.8, borderRadius: "50px", fontWeight: 700, fontSize: "0.95rem",
                    bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" },
                    boxShadow: "0 4px 16px rgba(21,101,192,0.35)",
                  }}>
                  {accountBusy
                    ? (accountMode === "login" ? "Logging you in…" : "Creating your account…")
                    : whishLoading ? "Redirecting to Whish…" : "Pay Now with Whish"}
                </Button>
                <Typography variant="caption" display="block" textAlign="center"
                  sx={{ mt: 2, color: "#9CA3AF" }}>
                  You'll be redirected to Whish's secure checkout to complete payment
                </Typography>
              </>
            ) : (
              <>
                <Button fullWidth variant="contained" size="large"
                  endIcon={accountBusy ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                  onClick={async () => { setError(""); if (await ensureAccount()) setStep(2); }}
                  disabled={accountBusy || activeMethods.length === 0}
                  sx={{
                    py: 1.8, borderRadius: "50px", fontWeight: 700, fontSize: "0.95rem",
                    bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" },
                    boxShadow: "0 4px 16px rgba(21,101,192,0.35)",
                  }}>
                  I've Sent the Payment — Continue
                </Button>
                <Typography variant="caption" display="block" textAlign="center"
                  sx={{ mt: 2, color: "#9CA3AF" }}>
                  After sending, click above to submit your payment confirmation
                </Typography>
              </>
            )}
          </Box>
        </Fade>
      )}

      {/* ── Step 2: Confirmation Form ─────────────────────────── */}
      {step === 2 && (
        <Fade in key="step2">
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ color: NAVY, mb: 0.5 }}>
              Confirm Your Payment
            </Typography>
            <Typography sx={{ color: MUTED, mb: 3 }}>
              Fill in your details and the transaction reference to complete your request.
            </Typography>

            <Stack spacing={2}>
              <TextField fullWidth label="Full Name" value={payerName} required
                onChange={e => setPayerName(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />

              <TextField fullWidth label="Phone Number" placeholder="+961 XX XXX XXX"
                value={payerPhone} required onChange={e => setPayerPhone(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />

              <TextField fullWidth label="Transaction Reference / Receipt Number"
                placeholder="e.g. WM-123456789" value={reference} required
                onChange={e => setReference(e.target.value)}
                helperText="Enter the reference or receipt number from your payment confirmation"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: "12px" }}>{error}</Alert>
            )}

            <Button fullWidth variant="contained" size="large"
              onClick={handleSubmit} disabled={loading}
              endIcon={loading
                ? <CircularProgress size={18} color="inherit" />
                : <SendIcon />}
              sx={{
                mt: 3, py: 1.8, borderRadius: "50px", fontWeight: 700,
                bgcolor: BLUE, "&:hover": { bgcolor: "#0D47A1" },
                boxShadow: "0 4px 16px rgba(21,101,192,0.35)",
                "&.Mui-disabled": { bgcolor: "#E8EDF5", color: "#90A4AE" },
              }}>
              {loading ? "Submitting..." : "Submit Payment Request"}
            </Button>

            <Typography variant="caption" display="block" textAlign="center"
              sx={{ mt: 2, color: "#9CA3AF" }}>
              Your account will be activated within 24 hours after manual verification
            </Typography>
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default PaymentRequest;
